import { defineStore } from 'pinia'
import { markRaw } from 'vue'

import { createLatestRequest } from '@/composables/latestRequest'
import { usePermission } from '@/composables/permission'
import { useApiStore } from '@/stores/api'

import { useSettingsStore } from './settings'
import { hotReloadStore } from '@/utils/build'

import type { LatestRequest } from '@/composables/latestRequest'
import type { HomePermission } from '@/composables/permission'
import type { ApplicationListDetail, ListApplicationsRequest } from '@policyfly/protobuf/patrick'
import type { VDataTableHeader } from 'vuetify/components/VDataTable'

export type TableHeader = VDataTableHeader & { visible?: boolean, disabled?: boolean }

export interface State {
  applications: ApplicationListDetail[]
  totalApplications: number
  rowsPerPage: number
  activeRequest: LatestRequest<[ListApplicationsRequest]>
  loading: boolean
  silent: boolean
  userHeaders: TableHeader[]
  defaultHeaders: TableHeader[]
}

export const useApplicationListStore = defineStore('applicationList', {
  state: (): State => ({
    applications: [],
    totalApplications: 0,
    rowsPerPage: 10,
    // holds functions and closure state rather than data, so there is nothing to make reactive
    activeRequest: markRaw(createLatestRequest({
      request: (controller, req: ListApplicationsRequest) => useApiStore().search.listApplications(req, controller),
      onSuccess: (response) => {
        // an options store has no `this` until its state exists, so look the store up once the response arrives
        const applicationListStore = useApplicationListStore()
        applicationListStore.applications = response.applications
        applicationListStore.totalApplications = response.count
      },
      onError: (err) => console.error(err),
    })),
    loading: true,
    // displays loading bar, but not loading template - keeps applications visible during background refresh
    silent: false,
    userHeaders: [],
    defaultHeaders: [
      { title: 'ID', key: 'primaryNamedInsured', align: 'left', sortable: false, disabled: true, visible: true },
      { title: 'Policy Number', key: 'externalId', align: 'left', sortable: false, visible: true },
      { title: 'Epic ID', key: 'epicId', align: 'left', sortable: false, visible: true },
      { title: 'Coverages', key: 'coverages', align: 'left', sortable: false, visible: true },
      { title: 'Carriers', key: 'carriers', align: 'left', sortable: false, visible: true },
      { title: 'Carriers', key: 'coverageCarriers', align: 'left', sortable: false, visible: true },
      { title: 'TIV', key: 'tiv', align: 'left', sortable: false, visible: true },
      { title: 'Effective', key: 'effective', align: 'left', sortable: true, visible: true },
      { title: 'Status', key: 'status', align: 'left', sortable: false, visible: true },
      { title: 'Market', key: 'facility', align: 'left', sortable: false, visible: true },
      { title: 'Assignee', key: 'assignee', align: 'left', sortable: false, visible: true },
      { title: 'Owner', key: 'ownedBy', align: 'left', sortable: false, visible: true },
      { title: 'Last Updated', key: 'modified', align: 'left', sortable: true, visible: true },
      { title: 'Priority Score', key: 'priorityScore', align: 'left', sortable: true, visible: true },
      { title: 'Notifications', key: 'notifications', align: 'center', sortable: false, visible: true },
    ],
  }),

  getters: {
    programHeaders (state): TableHeader[] {
      const settingsStore = useSettingsStore()
      const { checkPermission } = usePermission<HomePermission>()
      return state.defaultHeaders.filter((header) => {
        if (header.key === 'tiv' && !settingsStore.homeDisplay.tiv) return false
        if (header.key === 'coverages' && !settingsStore.homeDisplay.coverages) return false
        if (header.key === 'carriers' && !settingsStore.homeDisplay.carriers) return false
        if (header.key === 'coverageCarriers' && !settingsStore.homeDisplay.coverageCarriers) return false
        if (header.key === 'externalId' && !settingsStore.homeDisplay.externalId) return false
        if (header.key === 'epicId' && !settingsStore.homeDisplay.epicId) return false
        if (header.key === 'facility' && !settingsStore.homeDisplay.facility) return false
        if (header.key === 'priorityScore' && !checkPermission('homePriorityScore')) return false
        return true
      })
    },
    visibleHeaders: (state) => state.userHeaders.filter((h) => h.visible),
  },

  actions: {
    async loadApplications (req: ListApplicationsRequest, silent: boolean = false): Promise<void> {
      this.loading = true
      this.silent = silent
      await this.activeRequest.run(req)
      if (this.activeRequest.pending) return
      this.loading = false
      this.silent = false
    },
    cancelRequest (): void {
      this.activeRequest.abort()
      this.loading = false
    },
    clearList (): void {
      this.applications = []
      this.totalApplications = 0
      this.cancelRequest()
    },
    resetHeaders (): void {
      // user headers are persisted in local storage, when reloading make sure they are synced with program headers
      const programHeaders = this.programHeaders
      this.userHeaders = programHeaders.reduce((acc, programHeader) => {
        const existingHeader = this.userHeaders.find((uh) => uh.key === programHeader.key)
        // persist existing user headers, insert missing program headers
        // title always comes from the program header so translation changes are not masked by persisted state
        if (existingHeader) {
          acc.push({ ...programHeader, ...existingHeader, title: programHeader.title })
        } else {
          acc.push(programHeader)
        }
        return acc
      }, [] as TableHeader[])
    },
    toggleHeaderVisible (key: string): void {
      const target = this.userHeaders.find((h) => h.key === key)
      if (target && !target.disabled) target.visible = !target.visible
    },
  },
})

hotReloadStore(useApplicationListStore)
